import React, { useMemo, useState } from 'react';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../ui/form';
import { useForm } from 'react-hook-form';

import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { useDispatch } from 'react-redux';
import { doSetPremiumFeatures } from '../../modules/premiumFeatures/stores/thunk';
import { AppDispatch } from '../../redux/store';
import { Button } from '../ui/button';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import { addHours, format, isBefore } from 'date-fns';
import { CalendarIcon, CircleX } from 'lucide-react';
import { Calendar } from '../ui/calendar';

const formSchema = z.object({
  notes: z.optional(z.string()),
  status: z.boolean(),
  previewEnds: z.optional(z.date().nullable()),
});

export const PremiumFeatureForm = ({
  premiumFeature,
  update,
}: {
  premiumFeature: {
    feature: string;
    companyId: number;
    status: 'ACTIVE' | 'SUSPENDED';
    previewEnds?: Date;
    notes?: string;
  };
  update: () => void;
}) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      notes: premiumFeature.notes || '',
      status: premiumFeature.status === 'ACTIVE',
      previewEnds: premiumFeature.previewEnds,
    },
  });
  const dispatch = useDispatch<AppDispatch>();

  const setFeature = async (data: z.infer<typeof formSchema>) => {
    const input = {
      feature: premiumFeature.feature,
      companyId: premiumFeature.companyId,
      status: data.status ? 'ACTIVE' : 'SUSPENDED',
      previewEnds: data.status && data.previewEnds ? format(addHours(data.previewEnds, 12), 'yyyy-MM-dd') : null,
      notes: data.status ? data.notes : '',
    };
    form.reset({ status: data.status, previewEnds: data.status && data.previewEnds, notes: input.notes }); // reset with data to reset only isDirty

    await dispatch(doSetPremiumFeatures(input));
    update();
  };

  const disabled = useMemo(
    () => !form.formState.isDirty || !form.formState.isValid,
    [form.formState, form.formState.isDirty, form.formState.isValid], // apparently getters need explicity
  );

  const fieldsDisabled = useMemo(() => !form.getValues().status, [form.formState]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(setFeature)}>
        <div className="flex justify-between items-center">
          <div>
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      className="mx-4"
                      name={field.name}
                      id={field.name}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex-grow ml-4">
            <FormField
              control={form.control}
              name="previewEnds"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <Popover modal={true} open={calendarOpen} onOpenChange={(v) => !fieldsDisabled && setCalendarOpen(v)}>
                    <PopoverTrigger asChild>
                      <div className="flex items-center">
                        <Button
                          variant={'outline'}
                          type="button"
                          className={cn(
                            'w-[240px] pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground',
                            fieldsDisabled && 'cursor-not-allowed opacity-50',
                          )}
                        >
                          {field.value ? format(field.value, 'PPP') : <span>Ends per...</span>}
                          {!field.value ? (
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          ) : (
                            <div
                              className="ml-auto h-4 w-4"
                              onClick={(e) => {
                                e.stopPropagation();
                                form.setValue('previewEnds', null);
                              }}
                            >
                              <CircleX className="opacity-50" />
                            </div>
                          )}
                        </Button>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <FormControl>
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(d) => {
                            field.onChange(d);
                            setCalendarOpen(false);
                          }}
                          disabled={(date) => isBefore(date, new Date())}
                        />
                      </FormControl>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />
          </div>

          <div className="ml-4">
            <FormField
              control={form.control}
              name="notes"
              disabled={fieldsDisabled}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input className="rounded w-[320px]" placeholder="Notes..." {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div>
            <Button type="submit" variant="secondary" className="rounded ml-4" disabled={disabled}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};
